import { useMemo } from 'react';
import { useEmployees } from 'src/hooks/useEmployees';
import { useBranches } from 'src/hooks/useRestaurantBranches';

/**
 * The staff and issue report endpoints return employee and branch ids; the dashboards show
 * names. Same shape as `TopConsumedItems`' item lookup — a `Record<string, string>` built from
 * the list hook — and the same fallback: an id the lists no longer carry (a soft-deleted
 * employee or branch still owns its historical fines and absences) is shown as the id.
 *
 * Employee names follow the codebase's one fallback chain (`toEmployeeRow`, `MyEmployment`):
 * the linked account wins, the employment row's own columns cover account-less hires.
 */
export const useNameLookups = () => {
  const { data: employees } = useEmployees();
  const { data: branches } = useBranches();

  const employeeNames = useMemo(
    () =>
      (employees?.data || []).reduce(
        (acc, employee) =>
          Object.assign(acc, {
            [employee.id]: [
              employee.user?.firstName ?? employee.firstName,
              employee.user?.lastName ?? employee.lastName,
            ]
              .filter(Boolean)
              .join(' '),
          }),
        {} as Record<string, string>
      ),
    [employees?.data]
  );
  const branchNames = useMemo(
    () =>
      (branches?.data || []).reduce(
        (acc, branch) => Object.assign(acc, { [branch.id]: branch.name }),
        {} as Record<string, string>
      ),
    [branches?.data]
  );

  return {
    employeeName: (id: string) => employeeNames[id] || id,
    branchName: (id: string) => branchNames[id] || id,
  };
};
